/* eslint-disable @typescript-eslint/no-explicit-any */
const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const ITERATIONS = 100000;

function toBase64(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

function fromBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

async function deriveKey(password: string, salt: Uint8Array, exportable = false): Promise<CryptoKey> {
  const enc = new TextEncoder().encode(password);
  const km = await crypto.subtle.importKey(
    'raw',
    enc.buffer as ArrayBuffer,
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt.buffer as ArrayBuffer, iterations: ITERATIONS, hash: 'SHA-256' },
    km,
    { name: 'AES-GCM', length: 256 },
    exportable,
    exportable ? ['encrypt'] : ['encrypt', 'decrypt']
  );
}

export async function encrypt(data: string, password: string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const key = await deriveKey(password, salt);
  const encoded = new TextEncoder().encode(data);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv.buffer as ArrayBuffer }, key, encoded.buffer as ArrayBuffer);
  return [toBase64(salt.buffer as ArrayBuffer), toBase64(iv.buffer as ArrayBuffer), toBase64(ct)].join('.');
}

export async function decrypt(encryptedData: string, password: string): Promise<string> {
  const parts = encryptedData.split('.');
  if (parts.length !== 3) throw new Error('Invalid encrypted data format');
  const salt = fromBase64(parts[0]);
  const iv = fromBase64(parts[1]);
  const ct = fromBase64(parts[2]);
  const key = await deriveKey(password, salt);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv.buffer as ArrayBuffer }, key, ct.buffer as ArrayBuffer);
  return new TextDecoder().decode(pt);
}

export async function hashPassword(password: string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const key = await deriveKey(password, salt, true);
  const exported = await crypto.subtle.exportKey('raw', key);
  return [toBase64(salt.buffer as ArrayBuffer), toBase64(exported)].join('.');
}

export async function verifyPassword(password: string, hash: string): Promise<boolean> {
  const parts = hash.split('.');
  if (parts.length !== 2) return false;
  const salt = fromBase64(parts[0]);
  const key = await deriveKey(password, salt, true);
  const exported = await crypto.subtle.exportKey('raw', key);
  return toBase64(exported) === parts[1];
}
